package empiresmp.components.fade

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.CSSTransition
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.BoxScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateRows
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.thenIf
import empiresmp.components.fade.animation.Animatable
import empiresmp.components.fade.animation.InterpolateAnimator
import empiresmp.components.fade.animation.Transition
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun <T : Any> AnimatedVisibility(
    targetState: T?,
    modifier: Modifier = Modifier,
    contentKey: (T?) -> Any? = { targetState },
    fadeAnimation: Boolean = true,
    verticalAnimation: Boolean = true,
    horizontalAnimation: Boolean = true,
    content: @Composable BoxScope.(T) -> Unit,
) {
    val currentList = remember { mutableStateListOf<Transition<T>>() }

    LaunchedEffect(targetState) {
        val listsDeferred = currentList.toList()
            .filter { contentKey.invoke(it.instance) != contentKey.invoke(targetState) }
            .map { transition ->
                async {
                    transition.animatable.animateTo(targetValue = 0f, InterpolateAnimator(500.milliseconds))
                    currentList.remove(transition)
                }
            }

        targetState?.let {
            val wasInList = currentList
                .removeAll { contentKey.invoke(it.instance) == contentKey.invoke(targetState) }
            val lastTransition = Transition(
                targetState,
                Animatable(if (wasInList) 1f else 0f)
            )
            currentList.add(lastTransition)
            lastTransition.animatable.animateTo(targetValue = 1f, InterpolateAnimator(500.milliseconds))
        }
        listsDeferred.awaitAll()
    }
    if (!currentList.isEmpty()) {
        Box(
            modifier = modifier,
            content = {
                currentList.forEach { transition ->
                    Box(
                        modifier = Modifier
                            .thenIf(
                                condition = fadeAnimation,
                                other = Modifier.opacity(transition.animatable.value)
                            )
                            .thenIf(
                                condition = verticalAnimation,
                                other = Modifier
                                    .gridTemplateRows { minmax(0.px, transition.animatable.value.fr) }
                                    .transition(CSSTransition("grid-template-rows"))
                                    .overflow(Overflow.Hidden)
                            )
                            .thenIf(
                                condition = horizontalAnimation,
                                other = Modifier
                                    .gridTemplateColumns { minmax(0.px, transition.animatable.value.fr) }
                                    .transition(CSSTransition("grid-template-columns"))
                                    .overflow(Overflow.Hidden)
                            ),
                        contentAlignment = Alignment.Center,
                        content = {
                            content(transition.instance)
                        }
                    )
                }
            }
        )
    }
}
